import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  CreateReminderDtoCustomRecurrenceUnitEnum,
  CreateReminderDtoCustomRecurrenceWeekOfMonthEnum,
  CreateReminderDtoNotificationChannelsEnum,
  CreateReminderDtoRecurrenceTypeEnum,
  GetReminderDto,
} from 'woofwoof-api';
import * as Yup from 'yup';
import { translateKey } from 'src/locales/translate';
import {
  GuestReminderValidationCode,
  validateGuestReminder,
} from 'src/sections/@dashboard/visit/components/guestReminderValidation';

interface RemindersFormValues {
  description: string;
  scheduledAt: Date | null;
  recurrenceType: CreateReminderDtoRecurrenceTypeEnum;
  customEvery: number | undefined;
  customUnit: CreateReminderDtoCustomRecurrenceUnitEnum | undefined;
  customDayOfWeek: number | '';
  customWeekOfMonth: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum | '';
  emailEnabled: boolean;
  smsEnabled: boolean;
}

export interface RemindersFormSubmitValues {
  description: string;
  scheduledAt: string;
  recurrenceType: CreateReminderDtoRecurrenceTypeEnum;
  customRecurrence?: {
    every: number;
    unit: CreateReminderDtoCustomRecurrenceUnitEnum;
    dayOfWeek?: number;
    weekOfMonth?: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum;
  };
  notificationChannels: CreateReminderDtoNotificationChannelsEnum[];
}

const RECURRENCE_OPTIONS = [
  { value: CreateReminderDtoRecurrenceTypeEnum.Single, label: 'Jednorazowo' },
  { value: CreateReminderDtoRecurrenceTypeEnum.Daily, label: 'Codziennie' },
  {
    value: CreateReminderDtoRecurrenceTypeEnum.Monthly,
    label: translateKey('hardcoded.miesiecznie'),
  },
  { value: CreateReminderDtoRecurrenceTypeEnum.Yearly, label: 'Rocznie' },
  {
    value: CreateReminderDtoRecurrenceTypeEnum.Custom,
    label: 'Niestandardowe',
  },
];

const CUSTOM_UNIT_OPTIONS = [
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Hour, label: 'godziny' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Day, label: 'dni' },
  { value: CreateReminderDtoCustomRecurrenceUnitEnum.Week, label: 'tygodnie' },
  {
    value: CreateReminderDtoCustomRecurrenceUnitEnum.Month,
    label: translateKey('hardcoded.miesiace'),
  },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: translateKey('calendar.weekdayLongSun') },
  { value: 1, label: translateKey('calendar.weekdayLongMon') },
  { value: 2, label: translateKey('calendar.weekdayLongTue') },
  { value: 3, label: translateKey('calendar.weekdayLongWed') },
  { value: 4, label: translateKey('calendar.weekdayLongThu') },
  { value: 5, label: translateKey('calendar.weekdayLongFri') },
  { value: 6, label: translateKey('calendar.weekdayLongSat') },
];

const WEEK_OF_MONTH_OPTIONS = [
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.First,
    label: 'Pierwszym',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Second,
    label: 'Drugim',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Third,
    label: 'Trzecim',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Fourth,
    label: 'Czwartym',
  },
  {
    value: CreateReminderDtoCustomRecurrenceWeekOfMonthEnum.Last,
    label: 'Ostatnim',
  },
];

const buildSchema = (isEditing: boolean) =>
  Yup.object().shape({
    description: Yup.string()
      .required('Opis jest wymagany')
      .max(
        250,
        translateKey('hardcoded.opis_moze_miec_maksymalnie_250_znakow'),
      ),
    scheduledAt: isEditing
      ? Yup.date()
          .typeError(translateKey('hardcoded.podaj_prawidlowa_date_i_godzine'))
          .required(translateKey('hardcoded.data_i_godzina_sa_wymagane'))
      : Yup.date()
          .typeError(translateKey('hardcoded.podaj_prawidlowa_date_i_godzine'))
          .required(translateKey('hardcoded.data_i_godzina_sa_wymagane'))
          .min(
            new Date(),
            translateKey('hardcoded.data_nie_moze_byc_w_przeszlosci'),
          ),
    recurrenceType: Yup.string()
      .oneOf(
        Object.values(CreateReminderDtoRecurrenceTypeEnum),
        translateKey('hardcoded.nieprawidlowy_typ_powtarzania'),
      )
      .required(
        'Typ powtarzania jest wymagany',
      ) as Yup.StringSchema<CreateReminderDtoRecurrenceTypeEnum>,
    customEvery: Yup.number()
      .transform((value) => (isNaN(value) ? 1 : value))
      .when('recurrenceType', {
        is: CreateReminderDtoRecurrenceTypeEnum.Custom,
        then: (s) =>
          s
            .required(translateKey('hardcoded.wartosc_interwalu_jest_wymagana'))
            .min(1, 'Minimum 1')
            .max(365, 'Maksimum 365'),
        otherwise: (s) => s.default(1),
      }),
    customUnit: Yup.string().when('recurrenceType', {
      is: CreateReminderDtoRecurrenceTypeEnum.Custom,
      then: (s) =>
        s
          .oneOf(Object.values(CreateReminderDtoCustomRecurrenceUnitEnum))
          .required(
            'Jednostka jest wymagana',
          ) as Yup.StringSchema<CreateReminderDtoCustomRecurrenceUnitEnum>,
      otherwise: (s) =>
        s.optional() as Yup.StringSchema<CreateReminderDtoCustomRecurrenceUnitEnum>,
    }),
    customDayOfWeek: Yup.mixed<number | ''>().optional(),
    customWeekOfMonth: Yup.mixed<
      CreateReminderDtoCustomRecurrenceWeekOfMonthEnum | ''
    >().optional(),
    emailEnabled: Yup.boolean().required(),
    smsEnabled: Yup.boolean().required(),
  });

interface ProfileRemindersFormModalProps {
  isModalOpen: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  reminder?: GetReminderDto;
  canEdit?: boolean;
  singleReminderOnly?: boolean;
  closeModal: () => void;
  onSubmit: (values: RemindersFormSubmitValues) => void;
  onDelete: () => void;
}

export default function ProfileRemindersFormModal({
  isModalOpen,
  isCreating,
  isUpdating,
  isDeleting,
  reminder,
  canEdit = true,
  singleReminderOnly = false,
  closeModal,
  onSubmit,
  onDelete,
}: ProfileRemindersFormModalProps) {
  const isEditing = Boolean(reminder);
  const isLoading = isCreating || isUpdating;
  const [guestSubmitErrors, setGuestSubmitErrors] = useState<
    GuestReminderValidationCode[]
  >([]);

  const { control, handleSubmit, reset, watch, formState } =
    useForm<RemindersFormValues>({
      resolver: yupResolver(buildSchema(isEditing) as any),
      defaultValues: {
        description: '',
        scheduledAt: null,
        recurrenceType: CreateReminderDtoRecurrenceTypeEnum.Single,
        customEvery: 1,
        customUnit: CreateReminderDtoCustomRecurrenceUnitEnum.Week,
        customDayOfWeek: '',
        customWeekOfMonth: '',
        emailEnabled: true,
        smsEnabled: false,
      },
    });

  useEffect(() => {
    if (isModalOpen) {
      if (reminder) {
        reset({
          description: reminder.description,
          scheduledAt: new Date(reminder.scheduledAt),
          recurrenceType: singleReminderOnly
            ? CreateReminderDtoRecurrenceTypeEnum.Single
            : (reminder.recurrenceType as unknown as CreateReminderDtoRecurrenceTypeEnum),
          customEvery: singleReminderOnly
            ? 1
            : reminder.customRecurrence?.every ?? 1,
          customUnit: singleReminderOnly
            ? CreateReminderDtoCustomRecurrenceUnitEnum.Week
            : (reminder.customRecurrence
                ?.unit as CreateReminderDtoCustomRecurrenceUnitEnum) ??
              CreateReminderDtoCustomRecurrenceUnitEnum.Week,
          customDayOfWeek: singleReminderOnly
            ? ''
            : reminder.customRecurrence?.dayOfWeek ?? '',
          customWeekOfMonth: singleReminderOnly
            ? ''
            : (reminder.customRecurrence
                ?.weekOfMonth as CreateReminderDtoCustomRecurrenceWeekOfMonthEnum) ??
              '',
          emailEnabled: reminder.notificationChannels.includes(
            'EMAIL' as CreateReminderDtoNotificationChannelsEnum,
          ),
          smsEnabled: reminder.notificationChannels.includes(
            'SMS' as CreateReminderDtoNotificationChannelsEnum,
          ),
        });
      } else {
        reset({
          description: '',
          scheduledAt: new Date(),
          recurrenceType: CreateReminderDtoRecurrenceTypeEnum.Single,
          customEvery: 1,
          customUnit: CreateReminderDtoCustomRecurrenceUnitEnum.Week,
          customDayOfWeek: '',
          customWeekOfMonth: '',
          emailEnabled: true,
          smsEnabled: false,
        });
      }
    }
  }, [isModalOpen, reminder, reset, singleReminderOnly]);

  const recurrenceType = watch('recurrenceType');
  const customUnit = watch('customUnit');
  const isCustom =
    recurrenceType === CreateReminderDtoRecurrenceTypeEnum.Custom;
  const isCustomWeekly =
    isCustom && customUnit === CreateReminderDtoCustomRecurrenceUnitEnum.Week;
  const isCustomMonthly =
    isCustom && customUnit === CreateReminderDtoCustomRecurrenceUnitEnum.Month;

  const descriptionValue = watch('description');
  const scheduledAt = watch('scheduledAt');
  const emailEnabled = watch('emailEnabled');
  const smsEnabled = watch('smsEnabled');
  const guestValidationErrors = singleReminderOnly
    ? validateGuestReminder(
        {
          channels: [
            ...(emailEnabled
              ? [CreateReminderDtoNotificationChannelsEnum.Email]
              : []),
            ...(smsEnabled
              ? [CreateReminderDtoNotificationChannelsEnum.Sms]
              : []),
          ],
          scheduledAt,
        },
        new Date(),
      )
    : [];
  const guestErrors = Array.from(
    new Set([...guestValidationErrors, ...guestSubmitErrors]),
  );
  const guestScheduledAtIsPast = guestErrors.includes('past');
  const minScheduledAt = new Date();
  minScheduledAt.setSeconds(0, 0);

  useEffect(() => {
    if (!singleReminderOnly) {
      setGuestSubmitErrors([]);
      return;
    }

    const currentErrors = validateGuestReminder(
      {
        channels: [
          ...(emailEnabled
            ? [CreateReminderDtoNotificationChannelsEnum.Email]
            : []),
          ...(smsEnabled
            ? [CreateReminderDtoNotificationChannelsEnum.Sms]
            : []),
        ],
        scheduledAt,
      },
      new Date(),
    );

    setGuestSubmitErrors((errors) => {
      const remainingErrors = errors.filter((error) =>
        currentErrors.includes(error),
      );

      return remainingErrors.length === errors.length
        ? errors
        : remainingErrors;
    });
  }, [emailEnabled, scheduledAt, singleReminderOnly, smsEnabled]);

  const handleFormSubmit = (values: RemindersFormValues) => {
    const channels: CreateReminderDtoNotificationChannelsEnum[] = [];
    if (values.emailEnabled)
      channels.push(CreateReminderDtoNotificationChannelsEnum.Email);
    if (values.smsEnabled)
      channels.push(CreateReminderDtoNotificationChannelsEnum.Sms);

    if (singleReminderOnly) {
      const errors = validateGuestReminder(
        { channels, scheduledAt: values.scheduledAt },
        new Date(),
      );

      if (errors.length > 0) {
        setGuestSubmitErrors(errors);
        return;
      }
    }

    setGuestSubmitErrors([]);

    const payload: RemindersFormSubmitValues = {
      description: values.description,
      scheduledAt: (values.scheduledAt as Date).toISOString(),
      recurrenceType: singleReminderOnly
        ? CreateReminderDtoRecurrenceTypeEnum.Single
        : values.recurrenceType,
      notificationChannels: channels,
    };

    if (
      !singleReminderOnly &&
      values.recurrenceType === CreateReminderDtoRecurrenceTypeEnum.Custom
    ) {
      payload.customRecurrence = {
        every: values.customEvery!,
        unit: values.customUnit!,
        dayOfWeek:
          values.customDayOfWeek !== '' ? values.customDayOfWeek : undefined,
        weekOfMonth:
          values.customWeekOfMonth !== ''
            ? values.customWeekOfMonth
            : undefined,
      };
    }

    onSubmit(payload);
  };

  const channelError =
    singleReminderOnly && guestErrors.includes('required')
      ? translateKey('validation.guestReminderChannelRequired')
      : formState.isSubmitted && !emailEnabled && !smsEnabled
      ? translateKey('hardcoded.wybierz_co_najmniej_jeden_kanal_powiadomien')
      : undefined;

  const handleClose = () => {
    setGuestSubmitErrors([]);
    closeModal();
  };

  return (
    <Dialog open={isModalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {isEditing
            ? canEdit
              ? 'Edytuj przypomnienie'
              : translateKey('hardcoded.podglad_przypomnienia')
            : 'Dodaj przypomnienie'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} pt={1}>
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={translateKey('search.ui.aboutTab')}
                  multiline
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  disabled={!canEdit}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    `${descriptionValue.length}/250`
                  }
                  inputProps={{
                    maxLength: 250,
                    'data-testid': 'reminder-description',
                  }}
                />
              )}
            />

            <Controller
              name="scheduledAt"
              control={control}
              render={({ field, fieldState }) => (
                <MobileDateTimePicker
                  label="Data i godzina"
                  value={field.value}
                  onChange={field.onChange}
                  ampm={false}
                  minutesStep={1}
                  disabled={!canEdit}
                  minDateTime={minScheduledAt}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={
                        Boolean(fieldState.error) || guestScheduledAtIsPast
                      }
                      helperText={
                        fieldState.error?.message ??
                        (guestScheduledAtIsPast
                          ? translateKey(
                              'validation.guestReminderScheduledAtPast',
                            )
                          : undefined)
                      }
                    />
                  )}
                />
              )}
            />

            {singleReminderOnly ? (
              <TextField
                label="Powtarzanie"
                value="Jednorazowo"
                fullWidth
                InputProps={{ readOnly: true }}
                inputProps={{
                  'data-testid': 'reminder-recurrence-single',
                }}
              />
            ) : (
              <Controller
                name="recurrenceType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={Boolean(fieldState.error)}>
                    <InputLabel>Powtarzanie</InputLabel>
                    <Select {...field} label="Powtarzanie" disabled={!canEdit}>
                      {RECURRENCE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            )}

            {!singleReminderOnly && isCustom && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>Ustawienia niestandardowe</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name="customEvery"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label="Co ile"
                          type="number"
                          fullWidth
                          disabled={!canEdit}
                          inputProps={{ min: 1, max: 365 }}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Controller
                      name="customUnit"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl
                          fullWidth
                          error={Boolean(fieldState.error)}
                        >
                          <InputLabel>Jednostka</InputLabel>
                          <Select
                            {...field}
                            label="Jednostka"
                            disabled={!canEdit}
                          >
                            {CUSTOM_UNIT_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldState.error && (
                            <FormHelperText>
                              {fieldState.error.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {(isCustomWeekly || isCustomMonthly) && (
                    <Grid item xs={12}>
                      <Controller
                        name="customDayOfWeek"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel shrink>
                              {translateKey(
                                'hardcoded.dzien_tygodnia_opcjonalnie',
                              )}
                            </InputLabel>
                            <Select
                              {...field}
                              label={translateKey(
                                'hardcoded.dzien_tygodnia_opcjonalnie',
                              )}
                              displayEmpty
                              notched
                              disabled={!canEdit}
                            >
                              <MenuItem value="">Dowolny</MenuItem>
                              {DAY_OF_WEEK_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  )}

                  {isCustomMonthly && (
                    <Grid item xs={12}>
                      <Controller
                        name="customWeekOfMonth"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel shrink>
                              {translateKey(
                                'hardcoded.tydzien_miesiaca_opcjonalnie',
                              )}
                            </InputLabel>
                            <Select
                              {...field}
                              label={translateKey(
                                'hardcoded.tydzien_miesiaca_opcjonalnie',
                              )}
                              displayEmpty
                              notched
                              disabled={!canEdit}
                            >
                              <MenuItem value="">Dowolny</MenuItem>
                              {WEEK_OF_MONTH_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            <FormControl error={Boolean(channelError)} component="fieldset">
              <Typography>
                {translateKey('hardcoded.rodzaj_powiadomien')}
              </Typography>
              <FormGroup row>
                <Controller
                  name="emailEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={!canEdit}
                        />
                      }
                      label={translateKey('search.book.cardEmail')}
                    />
                  )}
                />
                <Controller
                  name="smsEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={!canEdit}
                        />
                      }
                      label="SMS"
                    />
                  )}
                />
              </FormGroup>
              {channelError && <FormHelperText>{channelError}</FormHelperText>}
            </FormControl>
          </Stack>
          {!canEdit && reminder && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {translateKey(
                'hardcoded.mozesz_edytowac_tylko_przypomnienia_dodane_w_ramach_twojej',
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Box>
            {isEditing && canEdit && (
              <LoadingButton
                variant="contained"
                color="error"
                data-testid="reminder-delete-open"
                onClick={onDelete}
                loading={isDeleting}
                disabled={isLoading}
              >
                {translateKey('common.remove')}
              </LoadingButton>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              disabled={isLoading || isDeleting}
            >
              {canEdit
                ? translateKey('common.cancel')
                : translateKey('common.close')}
            </Button>
            {canEdit && (
              <LoadingButton
                type="submit"
                variant="contained"
                data-testid="reminder-save"
                loading={isLoading}
                disabled={
                  isDeleting || (singleReminderOnly && guestErrors.length > 0)
                }
              >
                {isEditing
                  ? translateKey('common.save')
                  : translateKey('common.add')}
              </LoadingButton>
            )}
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}
